import {getFormattedFullLocation} from '@wix/wix-events-commons-statics'
import {ChevronUp} from '@wix/wix-ui-icons-common'
import {useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React from 'react'
import {getEventDateInformation, isMultiDay} from '../../../../../../../../../../../commons/selectors/event'
import {useWidgetActions, useWidgetState} from '../../../../../../../../hooks/state-provider'
import {useVisibilityStyles} from '../../../../../../../../hooks/use-visibility-styles'
import {getSelectedDate} from '../../../../../../../../selectors/calendar-layout'
import {getEventBadge, getMultiDayText, getRibbonData} from '../../../../../../../../selectors/events'
import {isMembersVisible, isRibbonVisible} from '../../../../../../../../selectors/settings'
import {EventBadge} from '../../../../../../../event-badge'
import {EventImage} from '../../../../../../../event-image'
import {LinkToPage} from '../../../../../../../link-to-page'
import {Members} from '../../../../../../../members'
import {Ribbon} from '../../../../../../../ribbon'
import {RsvpButton} from '../../../../../../../rsvp-button'
import {SocialBar} from '../../../../../../../social-bar'
import {MobileMonthlyEventDetailsProps} from './interfaces'
import s from './mobile-monthly-event-details.scss'

export const MobileMonthlyEventDetails = ({event}: MobileMonthlyEventDetailsProps) => {
  const {t} = useTranslation()
  const {
    allBreakpoints: {isEventBadgeInEventInfo},
  } = useVisibilityStyles()

  const {closeMonthlyCalendarEvent} = useWidgetActions()

  const hasImage = Boolean(event.mainImage)

  const selectedDate = useWidgetState(getSelectedDate)
  const multiDayText = useWidgetState(state => getMultiDayText(state, event.id, selectedDate, t))
  const hasRibbon = useWidgetState(state => getRibbonData(state, event)).visible
  const eventBadge = useWidgetState(state => getEventBadge(state, event))
  const showMembers = useWidgetState(state => isMembersVisible(state, event))
  const showRibbon = useWidgetState(state => isRibbonVisible(state, event))
  const date = useWidgetState(state => getEventDateInformation(state, event.id)).fullDate
  const multiDay = useWidgetState(state => isMultiDay(state, event))

  const handleEventClick = () => {
    closeMonthlyCalendarEvent()
  }

  return (
    <div className={s.container}>
      <div className={s.eventInfo} onClick={handleEventClick} data-hook={`event-details-${event.id}`}>
        {isEventBadgeInEventInfo(eventBadge?.type) && (
          <EventBadge event={event} className={classNames({[s.eventBadge]: !showRibbon || !hasRibbon})} />
        )}
        {showRibbon && <Ribbon event={event} className={s.ribbon} />}
        {multiDay ? (
          <div className={s.time}>{multiDayText}</div>
        ) : (
          <div className={s.fullDate} data-hook="date">
            {date}
          </div>
        )}
        <div
          className={classNames({
            [s.multiDayTitle]: multiDay,
            [s.title]: !multiDay,
          })}
          data-hook="title"
        >
          <LinkToPage event={event}>{event.title}</LinkToPage>
          <div className={s.chevron}>
            <ChevronUp size="1.2em" />
          </div>
        </div>
      </div>
      {hasImage && (
        <div className={s.image}>
          <EventImage event={event} />
        </div>
      )}
      {multiDay && (
        <div className={s.fullDate} data-hook="date">
          {date}
        </div>
      )}
      <div className={s.location} data-hook="location">
        {getFormattedFullLocation(event)}
      </div>
      {showMembers && (
        <div className={s.members} data-hook="members">
          <Members event={event} />
        </div>
      )}
      {event.description && (
        <div className={s.description} data-hook="description">
          {event.description}
        </div>
      )}
      <div className={s.share} data-hook="share">
        <div className={s.shareText}>{t('shareEventText')}</div>
        <SocialBar t={t} spacing={7} event={event} iconFontSize={14} newColors={false} row hideTitle />
      </div>
      <RsvpButton
        event={event}
        dataHook="rsvp-button"
        minWidth={88}
        maxWidth={170}
        minHeight={32}
        additionalStyle={{fontSize: 14}}
        containerClassName={s.button}
      />
    </div>
  )
}
